// Prize-wheel join kind and slice labels.
//
// ITS OWN SECTION, NOT A REGISTRATION TOGGLE. A show prints either ordinary
// registration or the wheel. The service picks the landing; this page only
// names the slices. Advertised to Nova admin and not refused here, on People's
// reasoning: the service answers a client viewer 403 in its own words.

import { NoticeBox, PageHead } from "@nova/desk-ui/parts";
import { SecLabel, Seg } from "@nova/desk-ui/kit";
import { Loading } from "@nova/desk-ui/states";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { listInstructions } from "../api/admin.js";
import { readWheel, saveWheel, type WheelDesk, type WheelJoinKind, type WheelSlice } from "../api/wheel.js";
import type { ApiOptions, Failure } from "../api/client.js";
import { WEB_ORIGIN } from "../build/thisBuild.js";
import { Qr } from "../qr/Qr.js";
import { encodeQr } from "../qr/encode.js";
import type { SessionToken } from "../session/store.js";
import { joinLink, standWheelLink, wheelLink } from "../../../web/src/link.js";
import { Refusal } from "./Refusal.js";
import { opens } from "./roleAccess.js";
import { useShow } from "./Show.js";

export type WheelProps = {
  readonly api: ApiOptions;
  readonly token: SessionToken;
  readonly activationId: string;
  readonly onSessionEnded: (detail: string) => void;
  readonly role: string;
  readonly onEnrol: () => void;
};

type Show =
  | { readonly phase: "reading" }
  | { readonly phase: "ready"; readonly desk: WheelDesk }
  | { readonly phase: "failed"; readonly failure: Failure };

const JOIN_LABEL: Readonly<Record<WheelJoinKind, string>> = {
  registration: "Registration",
  wheel: "Wheel",
};

const LIST_LEDE =
  "Which public URL this stand prints, the hall tablet address, and the labels on the dial. The service picks which slice lands. One spin per person.";

export function Wheel({
  api,
  token,
  activationId,
  onSessionEnded,
  role,
  onEnrol,
}: WheelProps): ReactNode {
  const frame = useShow();
  const [show, setShow] = useState<Show>({ phase: "reading" });
  // Bumped by "Ask again"; the read below runs again whenever it changes.
  const [attempt, setAttempt] = useState(0);
  const [joinKind, setJoinKind] = useState<WheelJoinKind>("registration");
  const [slices, setSlices] = useState<WheelSlice[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ readonly kind: "good" | "bad"; readonly detail: string } | null>(
    null,
  );
  /** True once Registration setup has been recorded. Null until that list returns. */
  const [registrationReady, setRegistrationReady] = useState<boolean | null>(null);

  useEffect(() => {
    if (frame.status !== "ready") return;

    let cancelled = false;
    setShow({ phase: "reading" });
    setNote(null);
    setRegistrationReady(null);
    void (async () => {
      const result = await readWheel(api, token, activationId);
      if (cancelled) return;
      if (!result.ok) {
        if (result.kind === "session_ended") onSessionEnded(result.detail);
        setShow({ phase: "failed", failure: result });
        return;
      }
      setJoinKind(result.value.joinKind);
      setSlices(result.value.slices.map((slice) => ({ id: slice.id, label: slice.label })));
      setShow({ phase: "ready", desk: result.value });
      // Whether Registration is set up, from its recorded setups, which are an
      // Admin's (roleAccess.ts, `registrationSetups`). An Editor runs the
      // wheel without being asked for them, and the page says nothing either
      // way rather than drawing a refusal under a desk that works.
      if (!opens({ role, canExport: null }, "registrationSetups")) return;
      const recorded = await listInstructions(api, token, activationId);
      if (cancelled) return;
      if (!recorded.ok) {
        if (recorded.kind === "session_ended") onSessionEnded(recorded.detail);
        setRegistrationReady(null);
        return;
      }
      setRegistrationReady(recorded.value.instructions.length > 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [api, token, activationId, onSessionEnded, frame.status, attempt, role]);

  if (frame.status === "failed") {
    return (
      <Refusal
        onRetry={frame.reload}
        what="Could not load the prize wheel"
        failure={frame.failure}
        role={role}
        onEnrol={onEnrol}
        notCounted="No slices were read, and none were saved, because the activation did not load."
      />
    );
  }

  if (show.phase === "reading") {
    return (
      <div className="stack">
        <PageHead title="Wheel" lede="Loading the prize wheel…" uuid={activationId} />
        <Loading rows={4} cols={2} />
      </div>
    );
  }

  if (show.phase === "failed") {
    return (
      <Refusal
        onRetry={() => setAttempt((n) => n + 1)}
        what="Could not load the prize wheel"
        failure={show.failure}
        role={role}
        onEnrol={onEnrol}
        notCounted="No slices were read, and none were saved, because the service did not answer this desk."
      />
    );
  }

  const desk = show.desk;
  const canSave = !busy && slices.length >= 4 && slices.length <= 12;

  const onSave = async () => {
    if (!canSave) return;
    setBusy(true);
    setNote(null);
    const result = await saveWheel(api, token, { activationId, joinKind, slices });
    setBusy(false);
    if (!result.ok) {
      if (result.kind === "session_ended") onSessionEnded(result.detail);
      setNote({ kind: "bad", detail: result.detail });
      return;
    }
    setShow({ phase: "ready", desk: result.value });
    setJoinKind(result.value.joinKind);
    setSlices(result.value.slices.map((slice) => ({ id: slice.id, label: slice.label })));
    setNote({ kind: "good", detail: "Saved. The printed codes follow the join kind on this page." });
  };

  return (
    <div className="stack">
      <PageHead title="Wheel" lede={LIST_LEDE} uuid={activationId} explain="info" />

      {registrationReady === false ? (
        <NoticeBox kind="amber" title="Set up Registration first">
          <p>
            The prize wheel uses the same Registration setup. Finish Registration setup and Publish
            Live before switching join kind to Wheel. Visitors opening the wheel link will otherwise
            see Not available.
          </p>
        </NoticeBox>
      ) : null}

      <section>
        <SecLabel info="Both public addresses are always live: /j/ is Registration and /w/ is Wheel. This setting only picks which public address the printed QR opens. Onsite Registration is always /s/ and onsite Wheel is always /sw/.">
          Join kind
        </SecLabel>
        <div className="panel panel-stack">
          <Seg
            label="What the printed QR opens"
            value={JOIN_LABEL[joinKind]}
            options={[JOIN_LABEL.registration, JOIN_LABEL.wheel]}
            onPick={(label) => setJoinKind(label === JOIN_LABEL.wheel ? "wheel" : "registration")}
          />
        </div>
      </section>

      <PrintedCode activationId={activationId} joinKind={joinKind} />

      <section>
        <SecLabel info="Equal odds, including blanks, until prizes are turned on: then the spin picks by the weights on Prizes. A blank label is kept and recorded. Between 4 and 12.">
          Slices
        </SecLabel>
        <div className="panel panel-stack">
          <p className="val">This stand has {desk.slices.length} slices.</p>
          <label className="field" style={{ maxWidth: 220 }}>
            <span className="label">How many slices</span>
            <select
              className="input"
              value={String(slices.length)}
              disabled={busy}
              onChange={(event) => setSlices(resizeSlices(slices, Number.parseInt(event.target.value, 10)))}
            >
              {Array.from({ length: 9 }, (_, at) => at + 4).map((count) => (
                <option key={count} value={String(count)}>
                  {count}
                </option>
              ))}
            </select>
          </label>
          <div className="slice-grid">
            {slices.map((slice, at) => (
              <label className="field" key={slice.id}>
                <span className="label">Slice {at + 1}</span>
                <input
                  className="input"
                  type="text"
                  maxLength={40}
                  value={slice.label}
                  disabled={busy}
                  onChange={(event) => {
                    const label = event.target.value;
                    setSlices(slices.map((row, index) => (index === at ? { ...row, label } : row)));
                  }}
                />
              </label>
            ))}
          </div>
          <div className="acts acts-flush">
            <button type="button" className="btn" disabled={!canSave} onClick={() => void onSave()}>
              {busy ? "Saving…" : "Save wheel"}
            </button>
          </div>
        </div>
      </section>

      {note === null ? null : (
        <NoticeBox kind={note.kind} title={note.kind === "good" ? "Wheel saved" : "Wheel not saved"}>
          <p>{note.detail}</p>
        </NoticeBox>
      )}
    </div>
  );
}

function PrintedCode({
  activationId,
  joinKind,
}: {
  readonly activationId: string;
  readonly joinKind: WheelJoinKind;
}): ReactNode {
  const origin = WEB_ORIGIN;
  const publicUrl = useMemo(() => {
    if (origin === null) return null;
    return joinKind === "wheel" ? wheelLink(origin, activationId) : joinLink(origin, activationId);
  }, [origin, activationId, joinKind]);
  // The other public address, which is live whether or not the QR points at
  // it. Join kind picks what gets printed; it does not switch the other one
  // off, so a stand can run the plain form on a badge desk and the wheel on the
  // dial at the same time. Shown rather than hidden because an address that
  // works and is not written down anywhere is one nobody uses.
  const wheelUrl = useMemo(
    () => (origin === null ? null : wheelLink(origin, activationId)),
    [origin, activationId],
  );
  const onsiteWheelUrl = useMemo(
    () => (origin === null ? null : standWheelLink(origin, activationId)),
    [origin, activationId],
  );
  const symbol = publicUrl === null ? null : encodeQr(publicUrl);

  if (publicUrl === null || symbol === null) {
    return (
      <section>
        <SecLabel>Printed code</SecLabel>
        <div className="panel">
          <p className="help">This console build has no public registration address yet.</p>
        </div>
      </section>
    );
  }

  return (
    <section>
      <SecLabel>Printed code</SecLabel>
      {/* The design's printed code: the symbol in its tile beside the
          addresses, each address a `.url` with the link as its `.id`. */}
      <div className="panel split">
        <div className="qr">
          {symbol.ok ? (
            <Qr
              matrix={symbol.matrix}
              label={
                joinKind === "wheel"
                  ? "A QR code pointing at this show's public prize wheel."
                  : "A QR code pointing at this show's public registration page."
              }
            />
          ) : (
            <NoticeBox kind="amber" title="Could not create the QR code">
              <p>{symbol.problem}</p>
            </NoticeBox>
          )}
        </div>
        <div className="urls">
          <div className="url">
            <p>
              {joinKind === "wheel"
                ? "Offsite Wheel. For visitors using their own phones. The QR opens this link. Email, code, details, terms, optional survey, then spin."
                : "The printed QR currently opens Offsite Registration. Offsite Wheel remains live below."}
            </p>
            <p className="id">{publicUrl}</p>
          </div>
          {wheelUrl === null || wheelUrl === publicUrl ? null : (
            <div className="url">
              <p><strong>Offsite Wheel.</strong> Visitors register on their own phone and then spin.</p>
              <p className="id">{wheelUrl}</p>
            </div>
          )}
          {onsiteWheelUrl === null ? null : (
            <div className="url">
              <p>
                <strong>Onsite Wheel.</strong> For a tablet in the hall. Open it separately in full
                screen. Details, terms, optional survey, then spin. There is no emailed code.
              </p>
              <p className="id">{onsiteWheelUrl}</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function resizeSlices(current: readonly WheelSlice[], count: number): WheelSlice[] {
  const next = Number.isInteger(count) && count >= 4 && count <= 12 ? count : current.length;
  const out = current.slice(0, next).map((slice) => ({ id: slice.id, label: slice.label }));
  while (out.length < next) {
    out.push({ id: `s${out.length + 1}`, label: "" });
  }
  return out;
}
